import { track } from './amplitude'
import { STORAGE_KEYS } from './constants'
import { AnalyticsEvent } from './events'

export type EmailProvider = 'gmail' | 'outlook' | 'other'
export type VerificationErrorType = 'invalid' | 'expired' | 'rate_limit'
export type ExtensionInstalledSource = 'content_script_ping' | 'runtime_message' | 'utm_param'

const EXTENSION_INSTALLED_FIRED_KEY = 'analytics_extension_installed_fired'

export const trackExtensionInstalledOnce = (source: ExtensionInstalledSource): void => {
    try {
        if (localStorage.getItem(EXTENSION_INSTALLED_FIRED_KEY) === 'true') return
        localStorage.setItem(EXTENSION_INSTALLED_FIRED_KEY, 'true')
    } catch {
        // localStorage unavailable — still track, accept potential duplicates
    }
    track(AnalyticsEvent.EXTENSION_INSTALLED, { source, browser_version: getBrowserVersion() })
}

export const markOnboardingStart = (): void => {
    try {
        localStorage.setItem(STORAGE_KEYS.ONBOARDING_START_TIME, String(Date.now()))
    } catch {
        // localStorage not available — ignore
    }
}

export const getOnboardingDurationSeconds = (): number | undefined => {
    try {
        const raw = localStorage.getItem(STORAGE_KEYS.ONBOARDING_START_TIME)
        if (!raw) return undefined
        const start = parseInt(raw, 10)
        if (!Number.isFinite(start) || start <= 0) return undefined
        const seconds = Math.max(0, Math.round((Date.now() - start) / 1000))
        localStorage.removeItem(STORAGE_KEYS.ONBOARDING_START_TIME)
        return seconds
    } catch {
        return undefined
    }
}

export const detectEmailProvider = (email: string | undefined | null): EmailProvider => {
    if (!email || typeof email !== 'string') return 'other'
    const domain = email.split('@')[1]?.toLowerCase() || ''
    if (domain.includes('gmail')) return 'gmail'
    if (domain.includes('outlook') || domain.includes('hotmail') || domain.includes('live.com')) return 'outlook'
    return 'other'
}

export const getBrowserVersion = (): string => {
    if (typeof navigator === 'undefined') return 'unknown'
    const ua = navigator.userAgent
    // Order matters: Edge UA also contains 'Chrome'
    const edge = ua.match(/Edg\/(\d+)/)
    if (edge) return `edge/${edge[1]}`
    const chrome = ua.match(/Chrome\/(\d+)/)
    if (chrome) return `chrome/${chrome[1]}`
    const firefox = ua.match(/Firefox\/(\d+)/)
    if (firefox) return `firefox/${firefox[1]}`
    const safari = ua.match(/Version\/(\d+).*Safari\//)
    if (safari) return `safari/${safari[1]}`
    return 'unknown'
}

/**
 * Signup OTP errors only. `classifyMcVerificationFailure` in ./mcVerification.ts does the same job
 * for the carrier MC flow with a wider bucket set — separate on purpose (different domains), but if
 * one gains a bucket, check whether the other needs it too.
 */
export const classifyVerificationError = (err: unknown): VerificationErrorType => {
    const msg = (err instanceof Error ? err.message : String(err ?? '')).toLowerCase()
    if (msg.includes('expired') || msg.includes('timeout')) return 'expired'
    if (msg.includes('rate') || msg.includes('too many') || msg.includes('429')) return 'rate_limit'
    return 'invalid'
}
